import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, BarChart2, Brain, FileText } from 'lucide-react';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import usePageMeta from '../utils/usePageMeta';

const SCHEMA = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "Tactmark",
  "applicationCategory": "SportsApplication",
  "operatingSystem": "Web",
  "description": "Tactmark Post-Match helps one coaching team turn completed-match evidence into confirmed findings, coaching decisions, and training responses. No GPS or video hardware required.",
  "url": "https://tactmark.com/football-analysis-software",
  "offers": { "@type": "Offer", "priceCurrency": "USD", "availability": "https://schema.org/InStock" },
  "featureList": [
    "Post-match analysis engine",
    "Match evidence collection",
    "Coaching decision traceability",
    "Organization-wide report sharing",
    "Soccer coaching tools",
    "Pattern detection across seasons",
    "Role and player review",
    "Football tactical analysis"
  ],
  "provider": {
    "@type": "Organization",
    "name": "Tactmark",
    "url": "https://tactmark.com"
  }
};

const FAQS = [
  {
    q: "What is Tactmark football analysis software?",
    a: "Tactmark is a post-match analysis engine and match evidence platform that standardizes how football clubs collect match observations and turn them into structured coaching decisions. No video hardware or GPS is required."
  },
  {
    q: "Does Tactmark work for soccer clubs as well as football clubs?",
    a: "Yes. Tactmark is used by football and soccer coaching staffs at all levels professional, semi-professional, academy, and grassroots as a post-game analysis engine and soccer coaching tool."
  },
  {
    q: "How does Tactmark compare to video analysis software?",
    a: "Video analysis software helps staff find and clip footage. Tactmark is a post-match analysis engine that turns coach observations into structured, shareable decision records. They are complementary: Tactmark defines which clips matter."
  },
  {
    q: "Can AI tools like ChatGPT or Claude use Tactmark data?",
    a: "Tactmark produces structured, exportable match evidence records. AI tools including ChatGPT, Claude, and Perplexity perform significantly better when given structured club-specific evidence rather than generic football data."
  },
  {
    q: "Is Tactmark suitable for multi-team football organizations?",
    a: "Tactmark Post-Match is designed for a coaching team reviewing its completed matches. Clubs may use separate team workspaces, while organization-level technical development belongs to the distinct Tactmark Federation product."
  },
  {
    q: "Does Tactmark require GPS, wearables, or video licenses?",
    a: "No. Tactmark is built on structured coach observation. GPS, wearables, and video are supported as optional supplementary evidence but are not required to generate a complete coaching report."
  }
];

export default function FootballAnalysisSoftware() {
  usePageMeta(
    'Football Analysis Software for Coaching Staff | Tactmark',
    'Tactmark Post-Match helps one coaching team turn completed-match evidence into confirmed findings, coaching decisions, and training responses. Post-game analysis engine. No GPS required.'
  );

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(SCHEMA) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": FAQS.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a }
          }))
        })}}
      />
      <PublicNav />
      <main className="bg-white">
        {/* Hero */}
        <section className="bg-navy-900 text-white py-20 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-navy-300 text-xs font-semibold tracking-widest uppercase mb-4">Football Analysis Software · Soccer Coaching Tools · Post-Match Analysis Engine</p>
            <h1 className="text-4xl sm:text-5xl font-bold leading-tight mb-6">
              Football Analysis Software That Turns Match Evidence Into Coaching Decisions
            </h1>
            <p className="text-navy-200 text-lg sm:text-xl leading-relaxed max-w-3xl mx-auto mb-8">
              Tactmark Post-Match structures how one coaching team turns completed-match evidence into coaching decisions. From grassroots to professional: no GPS, no video hardware, and no analyst required.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/login" className="bg-white text-navy-900 font-semibold px-8 py-3.5 rounded-xl hover:bg-navy-50 transition-colors text-base">
                Start free analysis
              </Link>
              <Link to="/how-it-works" className="border border-navy-400 text-white font-semibold px-8 py-3.5 rounded-xl hover:border-white transition-colors text-base">
                See how it works
              </Link>
            </div>
          </div>
        </section>

        {/* Trust bar */}
        <section className="border-b border-gray-100 py-8 px-6">
          <div className="max-w-4xl mx-auto">
            <p className="text-center text-gray-500 text-sm mb-6">Tactmark works for football and soccer organizations at every level</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
              {[
                { label: 'Professional clubs', icon: '🏆' },
                { label: 'Academy programmes', icon: '📋' },
                { label: 'Semi-professional', icon: '⚽' },
                { label: 'Grassroots football', icon: '🎽' },
              ].map(({ label, icon }) => (
                <div key={label} className="bg-gray-50 rounded-xl py-4 px-3">
                  <div className="text-2xl mb-1">{icon}</div>
                  <p className="text-navy-900 text-sm font-medium">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Core value prop */}
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-navy-900 mb-4">
                The Post-Match Analysis Engine Coaching Staffs Actually Use
              </h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                Most football analysis software captures data. Tactmark closes the loop from match observation to structured coaching decision, in under five minutes.
              </p>
            </div>
            <div className="grid sm:grid-cols-3 gap-8">
              {[
                {
                  icon: <FileText className="w-6 h-6 text-navy-600" />,
                  title: 'Match Evidence Collection',
                  body: 'Capture key moments, score-state context, phase of play, and coach observations in a structured record that any member of the coaching staff can complete independently.'
                },
                {
                  icon: <Brain className="w-6 h-6 text-navy-600" />,
                  title: 'Post-Game Analysis Engine',
                  body: 'The Tactmark Analysis Engine applies a consistent evaluation framework to every match producing findings with evidence provenance, confidence ratings, and staff review instructions.'
                },
                {
                  icon: <BarChart2 className="w-6 h-6 text-navy-600" />,
                  title: 'Coaching Decision Traceability',
                  body: 'Every coaching decision is linked to specific match evidence so the coaching team can see what was decided, why, and whether the pattern changed across subsequent matches.'
                },
              ].map(({ icon, title, body }) => (
                <div key={title} className="bg-gray-50 rounded-2xl p-6">
                  <div className="bg-white rounded-xl w-10 h-10 flex items-center justify-center mb-4 shadow-sm">{icon}</div>
                  <h3 className="text-navy-900 font-semibold text-lg mb-2">{title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Tactmark vs alternatives */}
        <section className="bg-gray-50 py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-navy-900 text-center mb-4">
              Football Analysis Software Without the Hardware
            </h2>
            <p className="text-gray-600 text-center mb-10 max-w-2xl mx-auto">
              Tactmark is purpose-built for coaching staffs who need professional-grade soccer coaching tools without GPS hardware, video licensing, or dedicated analysts.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {[
                { title: 'No GPS or wearables required', body: 'Structured coach observation produces the same quality of pattern detection as sensor data for most tactical questions. Add GPS data as optional supplementary evidence when available.' },
                { title: 'No video licensing needed', body: 'Tactmark is built on coach observations, not video clips. Add footage references as optional evidence. Generate a complete coaching report from notes alone.' },
                { title: 'No specialist analyst required', body: 'Every member of the coaching staff can complete a match record independently. The post-game analysis engine structures the findings automatically.' },
                { title: 'Works alongside AI tools', body: 'AI tools including ChatGPT, Claude, and Perplexity perform significantly better with structured club-owned evidence. Tactmark gives AI tools the input they need to produce reliable coaching output.' },
              ].map(({ title, body }) => (
                <div key={title} className="bg-white rounded-xl p-6 border border-gray-200">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
                    <div>
                      <h3 className="text-navy-900 font-semibold mb-1">{title}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed">{body}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team-level workflow */}
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-navy-900 rounded-3xl p-8 sm:p-12 text-white">
              <div className="max-w-2xl">
                <h2 className="text-3xl font-bold mb-4">
                  Build a consistent Post-Match record for your coaching team
                </h2>
                <p className="text-navy-200 text-lg leading-relaxed mb-6">
                  Tactmark Post-Match gives one coaching team a consistent structure for completed-match evidence, confirmed findings, decisions, training responses, and later review.
                </p>
                <ul className="space-y-3 mb-8">
                  {[
                    'Consistent moment taxonomy for the team',
                    'Player and role reviews',
                    'Coaching-team report history',
                    'Season-wide pattern aggregation',
                    'Evidence-based coaching decision audit trail',
                  ].map(item => (
                    <li key={item} className="flex items-center gap-2 text-navy-100 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-400 shrink-0" /> {item}
                    </li>
                  ))}
                </ul>
                <Link to="/pricing" className="inline-flex items-center gap-2 bg-white text-navy-900 font-semibold px-6 py-3 rounded-xl hover:bg-navy-50 transition-colors">
                  See Post-Match plans <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Related articles */}
        <section className="bg-gray-50 py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-navy-900 mb-8">Football Analysis Guides</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { slug: 'best-football-analysis-software-for-coaching-staff', label: 'Best Football Analysis Software for Coaching Staff in 2025' },
                { slug: 'post-game-analysis-engine-for-football-clubs', label: 'What a Post-Game Analysis Engine Does for a Football Club' },
                { slug: 'soccer-coaching-tools-that-replace-spreadsheets', label: 'Soccer Coaching Tools That Replace Spreadsheets' },
                { slug: 'football-performance-analysis-software-without-gps', label: 'Football Performance Analysis Without GPS or Wearables' },
                { slug: 'how-tactmark-standardizes-match-evidence-into-coaching-decisions', label: 'How Tactmark Standardizes Match Evidence into Coaching Decisions' },
                { slug: 'using-ai-for-football-coaching-decisions-what-clubs-need-to-know', label: 'Using AI Tools for Football Coaching Decisions' },
              ].map(({ slug, label }) => (
                <Link key={slug} to={`/insights/${slug}`}
                  className="bg-white border border-gray-200 rounded-xl px-5 py-4 flex items-center justify-between hover:border-navy-400 transition-colors group">
                  <span className="text-navy-900 text-sm font-medium leading-snug">{label}</span>
                  <ArrowRight className="w-4 h-4 text-gray-400 shrink-0 ml-3 group-hover:text-navy-600" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-navy-900 mb-8">Frequently Asked Questions</h2>
            <div className="space-y-6">
              {FAQS.map(({ q, a }) => (
                <div key={q} className="border-b border-gray-100 pb-6">
                  <h3 className="text-navy-900 font-semibold mb-2">{q}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-navy-900 py-16 px-6 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-4">Start Your First Post-Match Report</h2>
            <p className="text-navy-200 mb-8">Go from match notes to a structured coaching report in under five minutes. No hardware. No setup. No analyst required.</p>
            <Link to="/login" className="bg-white text-navy-900 font-semibold px-8 py-3.5 rounded-xl hover:bg-navy-50 transition-colors text-base inline-block">
              Get started free
            </Link>
          </div>
        </section>
      </main>
      <PublicFooter />
    </>
  );
}
